import { isMobileCameraBrowser } from "./cameraFacing";

/**
 * Um site não consegue escrever direto na galeria/fotos do celular — isso é
 * bloqueado por segurança em todo navegador. O caminho real em mobile é o
 * menu nativo de compartilhar (Web Share API com arquivo): a pessoa escolhe
 * "Salvar vídeo"/"Salvar em Fotos" e o sistema grava na galeria.
 *
 * Buscar o arquivo inteiro em memória (fetch → blob) só é seguro para
 * arquivos pequenos — acima disso um navegador de celular pode travar.
 * Por isso o compartilhamento com arquivo só é tentado até este tamanho;
 * gravações maiores abrem no player nativo, que também tem seu próprio
 * botão de salvar/compartilhar.
 */
const MAX_SHARE_BLOB_BYTES = 120 * 1024 * 1024; // 120 MB

export type RecordingDownloadOutcome = "shared" | "downloaded" | "opened" | "cancelled";

function isMobileDevice(): boolean {
    return isMobileCameraBrowser();
}

function canShareFiles(file: File): boolean {
    const nav = navigator as Navigator & {
        canShare?: (data?: ShareData) => boolean;
        share?: (data: ShareData) => Promise<void>;
    };
    if (typeof nav.share !== "function") return false;
    if (typeof nav.canShare === "function") {
        try {
            return nav.canShare({ files: [file] });
        } catch {
            return false;
        }
    }
    return true;
}

function triggerAnchorDownload(url: string, fileName: string): void {
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    link.rel = "noopener";
    document.body.appendChild(link);
    link.click();
    link.remove();
}

/**
 * No computador: baixa direto (vai para a pasta Downloads, como qualquer
 * arquivo). No celular: tenta entregar pelo menu de compartilhar quando o
 * arquivo é pequeno; senão abre o vídeo — o player do celular tem seu
 * próprio botão de salvar/compartilhar para a galeria.
 */
export async function saveRecordingToDevice(
    downloadUrl: string,
    fileName: string,
    fileSizeBytes: number | null,
): Promise<RecordingDownloadOutcome> {
    if (!isMobileDevice()) {
        triggerAnchorDownload(downloadUrl, fileName);
        return "downloaded";
    }

    const sizeKnownAndSmall =
        typeof fileSizeBytes === "number" && fileSizeBytes > 0 && fileSizeBytes <= MAX_SHARE_BLOB_BYTES;

    if (sizeKnownAndSmall) {
        try {
            const response = await fetch(downloadUrl);
            if (response.ok) {
                const blob = await response.blob();
                const file = new File([blob], fileName, { type: blob.type || "video/mp4" });
                if (canShareFiles(file)) {
                    try {
                        await (navigator as Navigator & { share: (data: ShareData) => Promise<void> }).share({
                            files: [file],
                            title: fileName,
                        });
                        return "shared";
                    } catch (shareError) {
                        if ((shareError as DOMException)?.name === "AbortError") {
                            return "cancelled";
                        }
                        // Falhou o compartilhamento com arquivo — cai para abrir o vídeo.
                    }
                }
            }
        } catch {
            // Falha ao buscar o blob (rede, CORS) — cai para abrir o vídeo.
        }
    }

    window.open(downloadUrl, "_blank", "noopener");
    return "opened";
}
